import debounce from 'lodash/debounce'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import fieldValidation from 'publisher/utils/fieldValidation'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalculateFormFieldStyles } from 'src/publisher/shared/hooks/use-calculate-form-fields-styles'
import { Field } from 'src/shared/components/field'
import { FieldIconNew } from 'src/shared/components/field-icon-new'
import { FieldWrapper } from 'src/shared/components/field-wrapper'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { CommonFieldSubEntityInterface } from 'src/shared/schemas/field/common-field-sub-entity.schema'

export interface CommonPublisherBaseFieldEntityOrSubEntityProps {
  entityOrSubEntity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | CommonFieldSubEntityInterface
  value: string | undefined
  onChange: (value: string | undefined) => void
  errors: string[]
  needInitialise?: boolean
  type?: string
  readOnly?: boolean
  disabled?: boolean
}

export const CommonPublisherBaseFieldEntityOrSubEntity = ({
  entityOrSubEntity,
  value,
  errors,
  onChange,
  needInitialise = true,
  type = 'text',
  readOnly,
  disabled,
}: CommonPublisherBaseFieldEntityOrSubEntityProps) => {
  const [isClient, setIsClient] = useState(false)
  const [valueState, setValueState] = useState(value)
  const [inlineErrors, setInlineErrors] = useState([])
  const { t } = useTranslation()

  const updateReduxValue = useCallback(
    debounce((value?: string) => {
      onChange(value)
    }, 100),
    [],
  )

  const handleChange = useCallback(
    (value?: string) => {
      if (entityOrSubEntity.slug) {
        setValueState(value)
        updateReduxValue(value)
      }
    },
    [updateReduxValue, entityOrSubEntity.slug],
  )

  const handleBlur = useCallback(
    (value?: string) => {
      const errors = fieldValidation(
        entityOrSubEntity.slug,
        value,
        entityOrSubEntity.optional,
      )
      setInlineErrors(errors.map((error: string) => t(error)))
      setTimeout(() => {
        window.dispatchEvent(new Event('form_updated'))
      }, 100)
    },
    [t, entityOrSubEntity.slug, entityOrSubEntity.optional],
  )

  const calculatedErrors = inlineErrors.length > 0 ? inlineErrors : errors

  // need to init all fields
  // biome-ignore lint/correctness/useExhaustiveDependencies: supposed to be fired once
  useEffect(() => {
    if (needInitialise) {
      // NOTE: we don't need debounce here
      onChange(value)
    }
  }, [needInitialise])

  useEffect(() => {
    if (value !== valueState) {
      setValueState(value)
    }
  }, [value])

  useEffect(() => {
    setIsClient(true)
  }, [])

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange(e.target.value)

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    handleBlur(e.target.value)

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedColor,
    calculatedBackgroundColor,
  } = useCalculateFormFieldStyles({
    border: entityOrSubEntity.border,
    mobileBorder: entityOrSubEntity.mobileBorder,
    padding: entityOrSubEntity.padding,
    mobilePadding: entityOrSubEntity.mobilePadding,
    fontSize: entityOrSubEntity.fontSize,
    mobileFontSize: entityOrSubEntity.mobileFontSize,
    lineHeight: entityOrSubEntity.lineHeight,
    mobileLineHeight: entityOrSubEntity.mobileLineHeight,
    textAlign: entityOrSubEntity.textAlign,
    mobileTextAlign: entityOrSubEntity.mobileTextAlign,
    color: entityOrSubEntity.color,
    mobileColor: entityOrSubEntity.mobileColor,
    backgroundColor: entityOrSubEntity.backgroundColor,
    mobileBackgroundColor: entityOrSubEntity.mobileBackgroundColor,
  })

  return (
    isClient && (
      <StyleWrapper
        margin={entityOrSubEntity.margin}
        mobileMargin={entityOrSubEntity.mobileMargin}
        appearance={entityOrSubEntity.appearance}
      >
        <FieldWrapper>
          {entityOrSubEntity.iconClassName && (
            <FieldIconNew
              color={entityOrSubEntity.color || 'initial'}
              fontSize={calculatedFontSize.desktop}
              mobileFontSize={calculatedFontSize.mobile}
              mobileColor={
                entityOrSubEntity.mobileColor ||
                entityOrSubEntity.color ||
                'initial'
              }
              className={entityOrSubEntity.iconClassName}
            />
          )}
          <Field
            id={entityOrSubEntity.htmlAttrId}
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            name={entityOrSubEntity.slug}
            type={type}
            placeholder={entityOrSubEntity.placeholder}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            placeholderColor={entityOrSubEntity.placeholderColor}
            mobilePlaceholderColor={entityOrSubEntity.mobilePlaceholderColor}
            onChange={onInputChange}
            onBlur={onInputBlur}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            boxShadow={entityOrSubEntity.boxShadow}
            mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
            hasErrors={calculatedErrors.length > 0}
            disabled={disabled}
            value={valueState || ''}
            readOnly={readOnly}
          />
        </FieldWrapper>
        <FieldErrors errors={calculatedErrors} />
      </StyleWrapper>
    )
  )
}
